/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import { Component, OnInit, AfterViewInit, OnDestroy, ViewChild } from '@angular/core';
import { AngularFireDatabase } from '@angular/fire/compat/database';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { fontManager } from '@appCanvasCommon/fontpath/FontManager';
import * as strings from '@touchplan/strings';
import {
	combineLatest,
	concat,
	Observable,
	of,
	Subject,
	Subscription,
	EMPTY,
	merge,
	BehaviorSubject,
	from,
	NEVER,
} from 'rxjs';
import {
	catchError,
	concatAll,
	debounceTime,
	distinctUntilChanged,
	filter,
	first,
	map,
	mapTo,
	mergeMap,
	pairwise,
	pluck,
	startWith,
	switchMap,
	take,
	takeUntil,
	withLatestFrom,
} from 'rxjs/operators';
import {
	FancySupplementalAction,
	SupplementalActionTypes,
} from 'src/app/app-data/actions/supplemental-actions';
import { CalendarWorkdays } from '@touchplan/ticket';
import { CanvasConnector } from 'src/app/app-data/models/CanvasConnector';
import { Dependencies } from 'src/app/app-data/models/Dependencies';
import { Plan, TicketRenderTypes, Ticket, PlanAccessLevel, PlanFields, TicketScale } from '@touchplan/common';
import { FancyActionTrackerFactory } from 'src/app/common/fancy-firebase/services/fancy-action-tracker-factory.service';
import { FbdbSettingsObject } from 'src/app/common/fancy-firebase/objects/FbdbSettingsObject';
import { PlanObject } from 'src/app/common/fancy-firebase/objects/PlanObject';
import { ProjectObject } from 'src/app/common/fancy-firebase/objects/ProjectObject';
import { makeStandardTicketJoiners } from 'src/app/common/fancy-firebase/util/utils';
import { perf } from 'src/app/plain-javascript/services/perfLog';
import { renderQueue } from 'src/app/plain-javascript/services/RenderQueue';
import { AppStateUser, AppState } from 'src/app/services/app-state.service';
import { PlanState, StateEvents } from 'src/app/services/plan-state.service';
import { PlanAccessCheck } from 'src/app/services/PlanAccessCheck';
import {
	FancyListIndexDumpFactory,
	FancyListIndexFragmenter,
	FirebaseListFactory,
} from 'src/app/common/fancy-firebase/services/factories';
import {
	PullColumnListFactory,
	TimelineTicketListFactory,
	ConstraintListFactory,
} from 'src/app/common/fancy-firebase-ticket/services/ticket-factories';
import { TicketEditService } from 'src/app/modules/ticket-edit/ticket-edit.service';

import { UndoPanelComponent } from 'src/app/common/undo/undo-panel/undo-panel.component';
import { PlanUndosService } from 'src/app/modules/plan/plan-undos.service';
import { FeatureFlagManager } from 'src/app/services/FeatureFlagManager';
import { DynamicFlagTypes } from '@touchplan/feature-flags';
import {
	LEVEL_OF_DETAIL_SIGNALS,
	LEVEL_OF_DETAIL_TABLE,
	LevelOfDetail,
	updateLodTableFromRemote,
} from '@appPlanApplication/lod';
import { ColorType } from 'src/app/app-data/constants/color-type.enum';
import { userProjectMemberObserver } from 'src/app/app-data/state/ProjectMembersApi';
import { FancyFirebaseSwimlanesApiBuilder } from 'src/app/services/fancy-firebase-api-builder/fancy-firebase-swimlanes-api-builder.service';
import { FancyFirebasePlansApiBuilder } from 'src/app/services/fancy-firebase-api-builder/fancy-firebase-plans-api-builder.service';
import { FancyFirebaseProjectMembersApiBuilder } from 'src/app/services/fancy-firebase-api-builder/fancy-firebase-project-members-api-builder.service';
import { FancyFirebaseProjectsApiBuilder } from 'src/app/services/fancy-firebase-api-builder/fancy-firebase-projects-api-builder.service';
import { FancyFirebaseColorsRolesLocationsApiBuilder } from 'src/app/services/fancy-firebase-api-builder/fancy-firebase-colors-roles-locations-api-builder.service';
import { FancyFirebaseCustomFieldsApiBuilder } from 'src/app/services/fancy-firebase-api-builder/fancy-firebase-customfields-api-builder.service';
import { FancyIdJoin } from 'src/app/common/fancy-firebase/base/FancyIdJoin';
import { FancyFirebaseList } from 'src/app/common/fancy-firebase/base/FancyFirebaseList';
import { FancyAction } from 'src/app/common/fancy-firebase/base/FancyAction';
import { fancyFirebaseGetters, fancyFirebaseReferencer } from '@touchplan/fancy-referencer';
import { TicketContext } from 'src/app/modules/ticket-edit-panel/ticket-context';
import { cssVar } from '@touchplan/css-var';
import { TicketListFactory } from 'src/app/common/fancy-firebase-ticket/services/ticket-factory';
import { ReportsUserPrefsService } from 'src/app/modules/reports/services/reports-user-prefs.service';
import { LoggerServiceFactory } from 'src/app/common/logging/LoggerServiceFactory';
import { DefaultLoggerService } from '@touchplan/logging';
import { ITempDock, PlanDockedTicketBinding } from 'src/app/modules/plan/plan-docked-ticket-binding.service';
import { ContainerType } from 'src/app/common/utility-components/base-container/ContainerLayerManager';
import { HoverOverStateService } from 'src/app/services/hover-over-state.service';
import {
	HoverOverUserPreferencesService,
	IHoverOverUserPreferences,
} from 'src/app/services/hover-over-preferences.service';
import { HoverOverCustomFieldManager } from '../../../services/hover-over-custom-field-manager.service';
import { UserPreferenceStateService } from '../../../services/user-preference-state.service';
import { analyticsEventsService } from 'src/app/plain-javascript/services/analytics/AnalyticsEventsService';
import { MatDialog } from '@angular/material/dialog';
import { openImportDialog } from 'src/app/modules/import-tickets/import-dialog-opener';
import { Location } from '@angular/common';
import { ImportTicketsCommonService } from '../../../modules/import-tickets/services/import-tickets-common.service';
import { PlanTicketsFilterRemoteControlService } from '../../../services/plan-tickets-filter-remote-control.service';
import { PlanPrintPanelRemoteControlService } from '../../../services/plan-print-panel-remote-control.service';
import { OrganizationDisclaimerService } from 'src/app/services/organization-disclaimer.service';
import { IFbdbTodayLine, TodayLineService } from '../../../modules/plan/services/today-line.service';
import firebase from 'firebase/compat/app';
import { PlanMilestone } from '../../../common/fancy-firebase/objects/PlanMilestone';
import { MilestoneLineService } from '../../../modules/plan/services/milestone-line.service';
import { ActiveLineObject } from '../../../common/fancy-firebase/objects/ActiveLineObject';
import { ActiveLineService } from '../../../modules/plan/services/active-line.service';
import { ActiveLineApi } from '../../../app-data/state/ActiveLineApi';
import { IFbdbPullSpace, PullSpaceService } from '../../../modules/plan/services/pull-space.service';
import { ActiveColumnService } from '../../../modules/plan/services/active-column.service';
import { ProjectWorkingDayService } from '../../../modules/plan/services/project-working-day.service';
import { TimelineTicketService } from '../../../modules/plan/services/timeline-ticket.service';
import { PromisePeriodList } from '../../../common/fancy-firebase/lists/PromisePeriodList';
import { RefOrObservable } from '../../../common/fancy-firebase/base/interfaces';
import { PromisePeriodService } from '../../../modules/plan/services/promise-period.service';
import { PromisePeriodApi } from '../../../app-data/state/PromisePeriodApi';
import { FirebaseSnapshotsService } from '../../../services/firebase-snapshots.service';
import { PlanPrintService } from 'src/app/modules/plan/services/plan-print.service';
import { GranularEnabledService } from 'src/app/common/granular-time/granular-enabled.service';
import { TicketEditCommonDesktopService } from '../../../common/docked-ticket/services/ticket-edit-common-desktop.service';

@Component({
	selector: 'plan',
	styleUrls: ['./plan.component.scss'],
	templateUrl: './plan.component.html',
	providers: [LoggerServiceFactory.provide('PlanComponent'), OrganizationDisclaimerService],
})
export class PlanComponent implements OnInit, AfterViewInit, OnDestroy {
	@ViewChild('undo', { static: false }) public undoPanel: UndoPanelComponent;

	private projectId: string;
	private planId: string;
	public shutdownSubject = new Subject<boolean>();
	public isHoverOverSettingsOpen = false;
	public isHoverLoadingAttachmentsData$ = new BehaviorSubject(true);

	private stuffToDestroy = [];
	private stuffToComplete = [];
	private unsubscribeList: Subscription[] = [];
	private preferences$ = this.userPreferenceStateService.preferences$;
	private hideCompletedTicketsChanged$ = this.userPreferenceStateService.hideCompletedTickets$;

	// BEGIN PLACEHOLDER REPLACE AS PART OF TP-4914
	public tempDock: ITempDock = {} as any;
	// END PLACEHOLDER REPLACE AS PART OF TP-4914

	//stuff
	public planPromptPosition: Observable<object>;
	public showPlanPrompt: Observable<boolean>; //not editing, not dragging, and ticket needs variance
	public undoEnabled$: Observable<boolean>;
	private dockedTicketEnabled$: Observable<boolean>;
	public showDockedTicket$: Observable<boolean>;
	public hoverOverEnabled$: Observable<boolean>;
	public hoverOverSettings$: Observable<Partial<IHoverOverUserPreferences>>;
	public hoverOverCustomFieldsData$: Observable<{ label: string; data: string }[]> = of([]);
	public hoverOverImageAttachmentsNumber$: Observable<number>;
	public ticketHoverData$: Observable<Ticket>;

	public ticketContext: TicketContext;

	public ticketEditPanelIsDisplayed$: Observable<boolean>;
	public draftsAreaHeight = cssVar.draftsAreaHeight;
	public hoverContainerType = ContainerType.HoverOver;

	constructor(
		private route: ActivatedRoute,
		protected appState: AppState,
		private titleService: Title,
		public planState: PlanState,
		private ticketListFactory: TicketListFactory,
		private angularFireDatabase: AngularFireDatabase,
		private fancyActionTrackerFactory: FancyActionTrackerFactory,
		private timelineTicketListFactory: TimelineTicketListFactory,
		private constraintListFactory: ConstraintListFactory,
		private fancyListIndexDumpFactory: FancyListIndexDumpFactory,
		private pullColumnListFactory: PullColumnListFactory,
		private ticketEditService: TicketEditService,
		private ticketEditCommonService: TicketEditCommonDesktopService,
		public planAccessCheck: PlanAccessCheck,
		private planUndosService: PlanUndosService,
		private featureFlagManager: FeatureFlagManager,
		private fancyFirebasePlansApiBuilder: FancyFirebasePlansApiBuilder,
		private fancyFirebaseProjectMembersApiBuilder: FancyFirebaseProjectMembersApiBuilder,
		private fancyFirebaseColorsRolesLocationsApiBuilder: FancyFirebaseColorsRolesLocationsApiBuilder,
		private fancyFirebaseProjectsApiBuilder: FancyFirebaseProjectsApiBuilder,
		private fancyFirebaseSwimlanesApiBuilder: FancyFirebaseSwimlanesApiBuilder,
		private fancyFirebaseCustomFieldsApiBuilder: FancyFirebaseCustomFieldsApiBuilder,
		private reportsUserPrefsService: ReportsUserPrefsService,
		private logger: DefaultLoggerService,
		private planDockedTicketBinding: PlanDockedTicketBinding,
		public hoverOverStateService: HoverOverStateService,
		public hoverOverUserPreferencesService: HoverOverUserPreferencesService,
		private hoverOverCustomFieldManager: HoverOverCustomFieldManager,
		private userPreferenceStateService: UserPreferenceStateService,
		private dialog: MatDialog,
		private router: Router,
		private _location: Location,
		private importTicketsCommonService: ImportTicketsCommonService,
		protected planTicketsFilterRemoteControlService: PlanTicketsFilterRemoteControlService,
		protected planPrintPanelRemoteControlService: PlanPrintPanelRemoteControlService,
		protected planPrintService: PlanPrintService,
		private organizationDisclaimerService: OrganizationDisclaimerService,
		private todayLineService: TodayLineService,
		private milestoneLineService: MilestoneLineService,
		private activeLineService: ActiveLineService,
		private pullSpaceService: PullSpaceService,
		private activeColumnService: ActiveColumnService,
		private projectWorkingDayService: ProjectWorkingDayService,
		private timelineTicketService: TimelineTicketService,
		private fancyListFactory: FirebaseListFactory,
		private promisePeriodService: PromisePeriodService,
		private firebaseSnapshot: FirebaseSnapshotsService,
		private granularEnabledService: GranularEnabledService
	) {
		perf.log('plan.component initialized');
		this.undoEnabled$ = this.featureFlagManager.getFlag$(DynamicFlagTypes.UNDO);
		this.dockedTicketEnabled$ = this.featureFlagManager.getFlag$(
			DynamicFlagTypes.DOCKED_TICKET_IN_DESKTOP_ENABLED
		);
		this.listenOpenImportTicketsDialog();
		this.listenToLodEmitter();
	}

	//not entirely sure _when_ I want to init this, so isolate the real code...
	public performSetup() {
		const userData: AppStateUser = this.route.snapshot.data['userData'];

		// Logging.notice(TEST_STRING);
		// Logging.notice(TEST_ARGS("blob"));
		const angularLand = {
			angularFireDatabase: this.angularFireDatabase,
			fancyActionTrackerFactory: this.fancyActionTrackerFactory,
		};
		const renderer$ = renderQueue.ob$.pipe(takeUntil(this.shutdownSubject));

		this.projectId = this.route.snapshot.paramMap.get('projectId');
		// We need to subscribe to the organization disclaimer to ensure that the user has accepted the disclaimer ASAP
		this.organizationDisclaimerService.subscribeAndShowOrganizationDisclaimer(
			this.projectId,
			this.appState.userSync.$id,
			this.planId
		);
		this.planId = this.route.snapshot.paramMap.get('planId');

		const planPath = fancyFirebaseGetters.getPlanPath(this.projectId, this.planId);
		const projectPath = fancyFirebaseGetters.getProjectsPath(this.projectId);
		const milestonePath: string = fancyFirebaseGetters.getMilestonePath(this.projectId, this.planId);

		if (!userData.indices.has(this.projectId)) {
			this.logger.log('user does not have a projectMember index, what do');
			return;
		}
		const projectMemberId = userData.indices.get(this.projectId).projectMemberId;

		const canvasConnector = new CanvasConnector();

		const roleColorsApi = this.fancyFirebaseColorsRolesLocationsApiBuilder.getColorsApi(
			this.projectId,
			ColorType.Roles
		);
		const locationsColorApi = this.fancyFirebaseColorsRolesLocationsApiBuilder.getColorsApi(
			this.projectId,
			ColorType.Locations
		);

		const rolesApi = this.fancyFirebaseColorsRolesLocationsApiBuilder.getRolesApi(
			this.projectId,
			roleColorsApi
		);
		const locationsApi = this.fancyFirebaseColorsRolesLocationsApiBuilder.getLocationsApi(
			this.projectId,
			locationsColorApi
		);

		const projectMembersApi = this.fancyFirebaseProjectMembersApiBuilder.getProjectMembersApi(
			this.projectId,
			rolesApi
		);
		const invitedUsersApi = this.fancyFirebaseProjectMembersApiBuilder.getInvitedUsersApi(
			this.projectId,
			rolesApi
		);

		const ticketExceptionDaysList = this.fancyFirebasePlansApiBuilder.getTicketExceptionDayList(
			this.projectId,
			this.planId
		);
		const ticketExceptionDaysJoiner = new FancyIdJoin('$id', ticketExceptionDaysList);

		const priorityList = this.fancyFirebaseProjectsApiBuilder.getPriorityList(this.projectId);
		const varianceList = this.fancyFirebaseProjectsApiBuilder.getVarianceList(this.projectId);

		const plan = new PlanObject(
			angularLand,
			fancyFirebaseReferencer.getLegacyRef<firebase.database.Reference>(planPath, this.projectId)
		);
		const project = new ProjectObject(
			angularLand,
			fancyFirebaseReferencer.getLegacyRef<firebase.database.Reference>(projectPath, this.projectId)
		);

		const plansApi = this.fancyFirebasePlansApiBuilder.getPlanApi(this.projectId);

		// Make sure we start loading the Reports UI prefs:
		this.reportsUserPrefsService.setIdsAndPreloadData(this.appState.userSync.$id, this.projectId);

		//used
		const pullColumnListMileStoneSupplement$ = plan.object$.pipe(
			map((plan) => {
				if (!plan || !plan.view.milestone) {
					return null;
				}
				return { x: plan.view.milestone.targetX, date: plan.view.milestone.targetDate };
			}),
			distinctUntilChanged((oldMill, newMill) => {
				const returnValue =
					oldMill === newMill ||
					(oldMill === null && newMill !== null) ||
					(newMill === null && oldMill !== null) ||
					oldMill.x !== newMill.x ||
					oldMill.date !== newMill.date;
				return !returnValue;
			}),
			map(
				(thing) => new FancySupplementalAction(thing, SupplementalActionTypes.milestoneUpdated, this.planId)
			)
		);
		// pullColumnListMileStoneSupplement$.subscribe(milestone => this.logger.log('milestone', milestone));

		const pullColumnList = this.pullColumnListFactory.create(
			fancyFirebaseReferencer
				.getLegacyRef<firebase.database.Reference>(planPath, this.projectId)
				.child('pullColumns')
		);
		pullColumnList.addSupplementalObservable(pullColumnListMileStoneSupplement$);

		const projectWorkdays = this.angularFireDatabase.object(
			fancyFirebaseReferencer
				.getLegacyRef<firebase.database.Reference>(projectPath, this.projectId)
				.child('/workShiftsByDow')
		);
		const projectExceptionsDaysPath = fancyFirebaseGetters.getProjectExceptionDaysPath(this.projectId);
		const projectExceptionDays = new FancyFirebaseList(
			angularLand,
			fancyFirebaseReferencer.getLegacyRef<firebase.database.Reference>(
				projectExceptionsDaysPath,
				this.projectId
			),
			FancyAction
		);
		const workDays = new CalendarWorkdays(
			rolesApi.data.list$.asObservable(),
			projectWorkdays.valueChanges(),
			projectExceptionDays.list$.asObservable() as Observable<Map<string, boolean>>
		);

		const ticketsPath = fancyFirebaseGetters.getTicketPath(this.projectId);
		const timelineTicketFragment = new FancyListIndexFragmenter(
			angularLand,
			fancyFirebaseReferencer.getLegacyRef<firebase.database.Reference>(ticketsPath, this.projectId)
		);

		const timelineTicketListJoiners = makeStandardTicketJoiners(
			rolesApi.data,
			locationsApi.data,
			projectMembersApi.data,
			priorityList
		);

		const timelineTicketList = this.timelineTicketListFactory.create(
			timelineTicketFragment.emitter$,
			timelineTicketListJoiners,
			[workDays, pullColumnList]
		);

		const projectRefPath = fancyFirebaseGetters.getTicketPath(this.projectId);
		timelineTicketList.projectRef = fancyFirebaseReferencer.getLegacyRef<firebase.database.Reference>(
			projectRefPath,
			this.projectId
		);

		const pullColumnCacheUpdated = pullColumnList.date$.pipe(
			map(
				(dates) => new FancySupplementalAction(dates, SupplementalActionTypes.pullColumnsUpdated, this.planId)
			)
		);
		timelineTicketList.addSupplementalObservable(pullColumnCacheUpdated as any);
		const planAccessUpdated$ = plansApi.planAccessApi?.data.list$.pipe(
			debounceTime(50),
			map(
				(accessList) =>
					new Map(
						Array.from(accessList).map(([planId, planAccess]) => [
							planId,
							planAccess.view.accessLevel === PlanAccessLevel.archived,
						])
					)
			),
			distinctUntilChanged((oldMap, newMap) => {
				if (oldMap.size !== newMap.size) {
					return false;
				}
				for (const [key, value] of oldMap) {
					if (newMap.get(key) !== value) {
						return false;
					}
				}
				return true;
			}),
			map(
				(planAccess) => new FancySupplementalAction(planAccess, SupplementalActionTypes.planArchivedChanged)
			)
		);
		timelineTicketList.addSupplementalObservable(planAccessUpdated$);

		timelineTicketFragment.start();

		const timelineTicketFragmentJoiner = new FancyIdJoin('$id', timelineTicketList, timelineTicketFragment);
		const timelineTicketsPath = fancyFirebaseGetters.getTimelineTicketsPath(this.projectId);
		const timelineTicketPlaceholderList = this.fancyListIndexDumpFactory.create(
			fancyFirebaseReferencer.getLegacyRef<firebase.database.Reference>(timelineTicketsPath, this.projectId),
			timelineTicketFragmentJoiner
		);

		// constraint list
		const constraintFragmenter = new FancyListIndexFragmenter(
			angularLand,
			fancyFirebaseReferencer.getLegacyRef<firebase.database.Reference>(ticketsPath, this.projectId)
		);

		const constraintListJoiners = makeStandardTicketJoiners(
			rolesApi.data,
			locationsApi.data,
			projectMembersApi.data,
			priorityList
		);
		const constraintList = this.constraintListFactory.create(
			constraintFragmenter.emitter$,
			constraintListJoiners,
			[workDays, pullColumnList]
		);
		constraintList.projectRef = fancyFirebaseReferencer.getLegacyRef<firebase.database.Reference>(
			projectRefPath,
			this.projectId
		);
		constraintList.addSupplementalObservable(planAccessUpdated$);

		constraintFragmenter.start();

		const constraintFragmentJoiner = new FancyIdJoin('$id', constraintList, constraintFragmenter);
		const constraintsPath = fancyFirebaseGetters.getConstraintsPath(this.projectId);
		const constraintPlaceholderList = this.fancyListIndexDumpFactory.create(
			fancyFirebaseReferencer.getLegacyRef<firebase.database.Reference>(constraintsPath, this.projectId),
			constraintFragmentJoiner
		);

		// const youObservable = observeYou(this.angularFireAuth.authState, projectMembersApi.data.rawEvent$);

		//setup project member sync
		const authedProjectMember$ = userProjectMemberObserver(this.appState.user, of(projectMembersApi));

		const setting$ = this.preferences$.pipe(
			takeUntil(this.shutdownSubject),
			map(
				(settings) =>
					new FancyAction<any>(settings, SupplementalActionTypes.sessionSettingsChanged, 'sessionSettings')
			)
		);

		//floating tickets list stuff
		const floatingTicketListJoiners = makeStandardTicketJoiners(
			rolesApi.data,
			locationsApi.data,
			projectMembersApi.data,
			priorityList
		);

		const projectMemberTicketsPath = fancyFirebaseGetters.getProjectMemberTicketsPath(
			this.projectId,
			projectMemberId
		);
		//can't actually use this with a syncronous reference to the projectMemberId at the time this component loads
		const floatingTicketList = this.ticketListFactory.create(
			fancyFirebaseReferencer.getLegacyRef<firebase.database.Reference>(
				projectMemberTicketsPath,
				this.projectId
			),
			floatingTicketListJoiners,
			[workDays]
		);
		floatingTicketList.suppressActionsForLoading = false;

		const ticketListJoiners = [
			...makeStandardTicketJoiners(rolesApi.data, locationsApi.data, projectMembersApi.data, priorityList),
			//only the normal ticket list currently needs this (hopefully we can restructure fbdb before anything else does)
			ticketExceptionDaysJoiner,
		];

		//ticket list stuff
		window.perf.log('ticketList initialized');
		const ticketListPath = fancyFirebaseGetters.getTicketPath(this.projectId, this.planId);
		const ticketList = this.ticketListFactory.create(
			fancyFirebaseReferencer.getLegacyRef<firebase.database.Reference>(ticketListPath, this.projectId),
			ticketListJoiners,
			[workDays]
		);

		const currentPlanAccessUpdated$ = planAccessUpdated$.pipe(
			map(({ payload }) => new Map([[this.planId, payload?.get(this.planId)]])),
			distinctUntilChanged((oldMap, newMap) => oldMap.get(this.planId) === newMap.get(this.planId)),
			map(
				(planAccess) => new FancySupplementalAction(planAccess, SupplementalActionTypes.planArchivedChanged)
			)
		);
		ticketList.addSupplementalObservable(currentPlanAccessUpdated$);
		ticketList.addSupplementalObservable(
			varianceList.list$.pipe(
				<any>map((list) => {
					return new FancySupplementalAction<Plan>(
						list as any,
						SupplementalActionTypes.varianceListUpdated,
						'varianceList'
					);
				})
			)
		);
		//used for hiding completed tickets
		ticketList.addSupplementalObservable(setting$);

		const scale = this.granularEnabledService.granularScale$.pipe(
			map(
				(scale) => new FancySupplementalAction<TicketScale>(scale, SupplementalActionTypes.ticketScaleChanged)
			)
		);
		ticketList.addSupplementalObservable(scale);

		//need to write a ticketCount property to /plan, subscribe to both tickets and plan, emit whenever the ticket count chnages and the plan value is not the same
		//set plan ticketCount property
		ticketList.list$
			.pipe(
				takeUntil(this.shutdownSubject),
				debounceTime(1000), //TODO - replace this
				withLatestFrom(plan.object$),
				mergeMap((arr) => {
					if (arr[0].size === 0) {
						return EMPTY;
					}
					if (arr[1] === null) {
						return EMPTY;
					}
					if (arr[0].size !== arr[1].view.ticketCount) {
						return of({ next: arr[0].size, prev: arr[1].view.ticketCount });
					}
					return EMPTY;
				}),
				filter((obj) => obj.next !== obj.prev)
				// distinctUntilChanged()
			)
			.subscribe((counts) => {
				plan.updateTicketCount(counts.next);
				// this.logger.log('ticketCount changed', counts.next);

				//special check for the first time it's set
				if (counts.next >= 1000 && (counts.prev === undefined || counts.prev === null)) {
					this.userPreferenceStateService.hideCompletedTickets = true;
					Logging.longWarning(strings.LARGE_PLAN_DETECTED_TEXT);
				}
			});

		plan.object$
			.pipe(
				filter((plan) => !!plan),
				take(1),
				takeUntil(this.shutdownSubject)
			)
			.subscribe((plan) => {
				if (plan.view.ticketCount && plan.view.ticketCount >= 1000) {
					this.userPreferenceStateService.hideCompletedTickets = true;
					Logging.longWarning(strings.LARGE_PLAN_DETECTED_TEXT);
				}
			});

		this.hideCompletedTicketsChanged$
			.pipe(
				filter((thing) => !!thing),
				switchMap((thing) => {
					return ticketList.selectedTicket$.pipe(
						take(1),
						filter((tickets) => !!tickets.list.size)
					);
				}),
				takeUntil(this.shutdownSubject)
			)
			.subscribe((thing) => {
				this.planState.actions.unSelectTicket(thing.list);
			});

		const selectionWithUpdate$ = ticketList.selectedTicket$.pipe(
			// tap(thing => this.logger.log('isssss filter?', thing)),
			filter((selection) => selection.list.size > 0),
			// tap(thing => this.logger.log('tapping', thing)),
			switchMap((selection) => {
				return concat(
					of(selection),
					ticketList.rawEvent$.pipe(
						// tap(thing => this.logger.log('sub tapping', thing)),
						filter((action) => selection.list.has(action.key)),

						mapTo(selection)
					)
				);
			})
		);

		this.planPromptPosition = selectionWithUpdate$.pipe(
			map((selection) => {
				let x = -Infinity;
				let y = Infinity;
				selection.list.forEach((t) => {
					if (t.view.left + t.view.width + 50 > x) {
						x = t.view.left + t.view.width + 50;
					}
				});
				selection.list.forEach((t) => {
					if (t.view.top < y) {
						y = t.view.top;
					}
				});
				return { x, y };
			})
		);

		this.showPlanPrompt = selectionWithUpdate$.pipe(
			map((selection) => {
				let show = false;
				selection.list.forEach((t) => {
					show = show || (t.renderType === TicketRenderTypes.database && t.view.needsVariance);
				});
				return show;
			})
		);

		const swimlanes = this.fancyFirebaseSwimlanesApiBuilder.getSwimlanesApi(
			this.projectId,
			this.planId,
			canvasConnector.cameraChange$.pipe(
				map((viewport) => new FancySupplementalAction(viewport, SupplementalActionTypes.cameraChanged))
			)
		);

		const customFields = this.fancyFirebaseCustomFieldsApiBuilder.getCustomFieldsApi(
			this.projectId,
			this.planId,
			projectMemberId
		);

		const laborCounts = this.fancyFirebasePlansApiBuilder.getLaborCountsApi(this.projectId, this.planId);
		// MOC-3024 - The reason why I create a second list is to access the project wide labor count. I don't do it in the above list
		// because then we are watching updates for _all_ plans in a project, due to the way the data is structured.
		const projectLaborCounts = this.fancyFirebasePlansApiBuilder.getLaborCountsApi(
			this.projectId,
			'allPlans'
		);

		// MOC-2875 - Create a list for fbdbSettings
		const settingsPath = fancyFirebaseGetters.getSettingsPath();
		const fbdbSettingsObject = new FbdbSettingsObject(
			angularLand,
			fancyFirebaseReferencer.getLegacyRef<firebase.database.Reference>(settingsPath, this.projectId)
		);

		// MOC-2875 - We have an fbdbSettings list, but no easy way to access a specific value from the list in AngularJS-land.
		// So this little hack sets the value of settings/crewSizesEnabled to the sessionStorage.
		// When the plan-menu and plan-actions-menu is ported over to Angular, remove this and update the ng-show to use (planState.fbdbSettings.list.list$ | async)?

		const supTicketPlanEvents = plan.object$.pipe(
			map((data) => {
				return new FancySupplementalAction<Plan>(data, SupplementalActionTypes.planUpdated, this.planId);
			})
		);
		//calcs side, pins, longest ticket in column
		ticketList.addSupplementalObservable(supTicketPlanEvents as any);

		const dependencies = new Dependencies(ticketList, timelineTicketList, this.planId);

		dependencies.mode
			.pipe(takeUntil(this.shutdownSubject), pluck('isAllType'), distinctUntilChanged())
			.subscribe((allOn: boolean) => {
				this.userPreferenceStateService.showAllDependencies = allOn;
			});

		//TODO - replace with projectState
		combineLatest([plan.object$, project.object$])
			.pipe(takeUntil(this.shutdownSubject))
			.subscribe((objs) => {
				if (!objs[0] || !objs[1] || !objs[1].projectName) {
					return;
				}
				const projectName = objs[1].projectName;
				const planName = objs[0].view.name;
				this.titleService.setTitle(planName + ' - ' + projectName + ' - Touchplan');
			});

		// ticketList.rawEvent$.pipe(bufferTime(50), filter(thing => !!thing.length)).subscribe(thing => this.logger.log(thing))
		window.perf.merge(
			ticketList.list$.pipe(
				debounceTime(5300),
				map((thing) => ({
					name: "plan loaded 'tickets settled heuristic'",
					extraData: { timestampOffset: 3000, previousEventName: 'plan.component initialized' },
				})),
				take(1)
			)
		);

		const planAccess$ = plansApi.planAccessApi.data.list$.pipe(map((list) => list.get(this.planId)));

		this.planAccessCheck.start(planAccess$, project.object$.asObservable(), authedProjectMember$);

		//-----------------------------------------------------------------------
		// Undo setup
		this.undoEnabled$.pipe(takeUntil(this.shutdownSubject)).subscribe((enabled: boolean) => {
			if (enabled) {
				this.planUndosService.setupOperations(ticketList, plan, this.planState, this.ticketEditCommonService);
			} else {
				this.planUndosService.destroyOperations();
			}
		});

		const lodSettingsPath = fancyFirebaseGetters.getSettingsPath('lodSettings');
		//debug lod thing
		this.angularFireDatabase
			.object(
				fancyFirebaseReferencer.getLegacyRef<firebase.database.Reference>(lodSettingsPath, this.projectId)
			)
			.valueChanges()
			.pipe(takeUntil(this.shutdownSubject))
			.subscribe((s) => {
				updateLodTableFromRemote(s as any);
			});

		// Generate IFbdbTodayLine from plan object and then register with TodayLineService
		const todayLine$: Observable<IFbdbTodayLine | null> = plan.object$.pipe(
			map((plan: Plan | null) => {
				if (!plan) {
					return null;
				}
				return {
					fbdbTime: plan.view.fbdbTime,
					fbdbTimeX: plan.view.fbdbTimeX,
				};
			})
		);
		this.todayLineService.registerPlanData(todayLine$);

		// Generate IFbdbPullSpace from plan object and then register with PullSpaceService
		const pullColumns$: Observable<IFbdbPullSpace | null> = plan.object$.pipe(
			map((plan: Plan | null) => {
				if (!plan) {
					return null;
				}
				return {
					columns: plan.view.pullColumns,
					pullFinishX: plan.view.pullFinishX,
					pullTicketHeightPixels: plan.view.pullTicketHeightPixels,
					pullTicketWidthPixels: plan.view.pullTicketWidthPixels,
				};
			})
		);
		this.pullSpaceService.registerPlanData(pullColumns$);

		// TP-6730: New active column service
		this.activeColumnService.registerPlanData();
		// TP-6927: New Project Working Days service
		this.projectWorkingDayService.registerPlanData(authedProjectMember$, workDays);

		this.planState.setup(
			this.route,
			canvasConnector,
			ticketList,
			timelineTicketList,
			constraintList,
			floatingTicketList,
			rolesApi,
			locationsApi,
			varianceList,
			plansApi,
			projectMembersApi,
			invitedUsersApi,
			priorityList,
			plan,
			workDays,
			authedProjectMember$,
			dependencies,
			swimlanes,
			laborCounts,
			projectLaborCounts,
			ticketExceptionDaysList,
			fbdbSettingsObject,
			project,
			customFields,
			roleColorsApi,
			locationsColorApi
		);

		// TP-6813: Canvas-powered timeline planMilestone firebase object
		const planMilestoneObject: PlanMilestone = new PlanMilestone(
			angularLand,
			fancyFirebaseReferencer.getLegacyRef<firebase.database.Reference>(milestonePath, this.projectId)
		);
		this.milestoneLineService.registerPlanData(planMilestoneObject, this.planState);

		// TP-6814: Active Line Service
		const activeLineObject: ActiveLineObject = new ActiveLineObject(
			angularLand,
			fancyFirebaseReferencer.getLegacyRef<firebase.database.Reference>(planPath, this.projectId)
		);
		const activeLineApi: ActiveLineApi = new ActiveLineApi(activeLineObject);
		this.activeLineService.registerPlanData(activeLineObject, this.planState, activeLineApi);

		// TP-6767: Timeline Ticket Service
		this.timelineTicketService.registerPlanData(timelineTicketList);

		// TP-6739: Promise Period Service
		const snapshotPath: RefOrObservable = fancyFirebaseReferencer.getLegacyRef<firebase.database.Reference>(
			`${planPath}/promisePeriodStack`,
			this.projectId
		);
		const promisePeriodList: PromisePeriodList = this.fancyListFactory.create(
			PromisePeriodList,
			snapshotPath
		);
		const promisePeriodApi: PromisePeriodApi = new PromisePeriodApi(
			promisePeriodList,
			plansApi,
			activeLineObject,
			this.planAccessCheck,
			this.firebaseSnapshot,
			project
		);
		this.promisePeriodService.registerPlanData(promisePeriodList, promisePeriodApi, plan);

		this.planState.stateChange$.pipe(takeUntil(this.shutdownSubject)).subscribe((state) => {
			if (StateEvents.BOOMED === state) {
				this.destroyStuff();
			}
		});

		this.stuffToDestroy.push(...ticketListJoiners);
		this.stuffToDestroy.push(...timelineTicketListJoiners);
		this.stuffToDestroy.push(...constraintListJoiners);
		this.stuffToDestroy.push(...floatingTicketListJoiners);

		this.stuffToDestroy.push(
			timelineTicketFragmentJoiner,
			constraintFragmentJoiner,
			pullColumnList,
			timelineTicketFragment,
			timelineTicketPlaceholderList,
			constraintFragmenter,
			constraintPlaceholderList,
			projectExceptionDays,
			this.todayLineService,
			this.milestoneLineService,
			this.pullSpaceService,
			this.activeLineService,
			this.activeColumnService,
			this.projectWorkingDayService,
			this.timelineTicketService,
			this.promisePeriodService
		);

		this.ticketContext = new TicketContext();
		this.ticketContext.projectId = this.projectId;
		this.ticketContext.planId = this.planId;
		// Set "userId" once it's loaded:
		this.planState.authedProjectMember$
			.pipe(
				filter((userId) => !!userId),
				take(1)
			)
			.subscribe((authedPM) => {
				this.ticketContext.userId = authedPM.view.userId;
			});
		this.ticketContext.initTicketFields(
			this.ticketEditCommonService.getSingleEditTicket$(),
			this.planAccessCheck
		);

		// BEGIN PLACEHOLDER REPLACE AS PART OF TP-7386
		// This and its usages can be removed once docket ticket completed,
		// and we don't need the old edit attachment panel
		this.ticketEditPanelIsDisplayed$ = this.ticketEditService.ticketEditPanelIsDisplayed$;
		// END PLACEHOLDER REPLACE AS PART OF TP-7386

		this.hoverOverEnabled$ = this.appState.userId.pipe(
			switchMap((userId) => this.hoverOverUserPreferencesService.isHoverOverEnabled$(userId, this.projectId))
		);

		this.hoverOverSettings$ = this.appState.userId.pipe(
			first((userId) => !!userId),
			switchMap((userId) =>
				this.hoverOverUserPreferencesService.hoverOverUserProjectPreferencesChanges$(userId, this.projectId)
			)
		);

		this.hoverOverImageAttachmentsNumber$ = this.hoverOverStateService.hoverOverTicketId$.pipe(
			filter((id) => !!id),
			switchMap((id) =>
				this.planState.fileAttachCloneService.getTicketAttachment$(this.projectId, this.planId, id)
			),
			map((attachments) => {
				const imagesArr = Object.values(attachments)[0];
				return !!imagesArr ? Object.keys(imagesArr).length : 0;
			})
		);

		// emits the full ticket data for hover over
		this.ticketHoverData$ = this.hoverOverStateService.hoverOverTicketId$.pipe(
			// null emission is required to reset a previous emited data
			startWith(null as string),
			pairwise(),
			switchMap(([oldId, newId]) => {
				if (!newId) {
					return of(null);
				} else if (oldId !== newId) {
					return from([of(null), this.planState.tickets.getChildOb$(newId).pipe()]).pipe(concatAll());
				} else {
					return this.planState.tickets.getChildOb$(newId);
				}
			}),
			//{ ...data.payload } as Ticket
			// is suuuuuper incorrect, it's more of a Ticket-like, but we don't have that
			// and this was already here
			map((data) => (data?.payload ? ({ ...data.payload } as Ticket) : (null as Ticket)))
		);

		// emits custom fields data for hover over
		this.hoverOverCustomFieldsData$ = this.ticketHoverData$.pipe(
			switchMap((ticket) => {
				if (!ticket) {
					// null emission is required to reset a previous emited data
					return of(null);
				} else {
					return of(ticket).pipe(
						distinctUntilChanged((prevTicket, currTicket) => prevTicket.$id === currTicket.$id),
						switchMap((ticket) =>
							this.appState.userId.pipe(
								switchMap((userId) =>
									this.hoverOverCustomFieldManager.getHoverOverCustomFieldsData$(
										userId,
										this.projectId,
										ticket
									)
								)
							)
						)
					);
				}
			})
		);

		merge(
			this.hoverOverImageAttachmentsNumber$.pipe(mapTo(false)),
			this.hoverOverStateService.combinedHoverVisibilityObservable$.pipe(
				filter((isShown) => !isShown),
				mapTo(true),
				takeUntil(this.shutdownSubject)
			)
		).subscribe(this.isHoverLoadingAttachmentsData$);

		// BEGIN PLACEHOLDER REPLACE AS PART OF TP-4914
		//temp docked ticket stuff
		this.dockedTicketEnabled$
			.pipe(
				map((val) => !!val), //convert nulls into falses
				distinctUntilChanged(),
				catchError((e, ob$) => {
					//if there's an error, log it, and give it false (feature off)
					//will disable the feature, and then not fire any more without a refresh
					this.logger.error(e);
					return of(false);
				})
			)
			.subscribe((enableMasterSchedule) => {
				if (this.tempDock && this.tempDock.destroy) {
					this.tempDock.destroy();
				}

				if (enableMasterSchedule) {
					this.tempDock = this.planDockedTicketBinding.startMonitoring();
					window.tempDock = this.tempDock;
				}
			});
		// END PLACEHOLDER REPLACE AS PART OF TP-4914
	}

	public onTurnOffHoverOver() {
		this.hoverOverUserPreferencesService.updateHoverOverUserProjectPreferences(
			this.ticketContext.userId,
			this.planState.projectId,
			{ isEnabled: false }
		);
		this.hoverOverStateService.endHoverComponent();
		analyticsEventsService.setHoverVisibility(false);
	}

	public showHoverOverSettings(showHoverOver: boolean) {
		this.isHoverOverSettingsOpen = showHoverOver;
	}

	public ngOnInit() {
		this.performSetup();
		this.dockedTicketEnabled$.subscribe((enabled) => {
			if (enabled) {
				this.showDockedTicket$ = combineLatest([
					this.tempDock.ticket$,
					this.planState.tickets.selectedTicket$,
				]).pipe(
					map(
						([ticket, selectedTickets]) =>
							// BEGIN PLACEHOLDER REPLACE AS PART OF TP-7190
							!!ticket?.srcList?.size && ticket.srcList.size <= 1 && selectedTickets.list.size <= 1
						// END PLACEHOLDER REPLACE AS PART OF TP-7190
					),
					takeUntil(this.shutdownSubject)
				);
			} else {
				this.showDockedTicket$ = of(false);
			}
		});
	}

	public ngAfterContentInit() {
		// this.logger.log('ngAfterContentInit');
	}

	//if we delay setup until here, the scoupSoup will be defined
	// this means that the angular code is technically initialized and run before planState is considered ready
	// this may or may not ever be a problem
	public ngAfterViewInit() {
		// this.logger.log('ngAfterViewInit');
		// this.performSetup();

		this.planState.toggleUndoPanel = () => this.undoPanel.togglePanel();
	}

	//don't want to override an internal destroy
	public destroyStuff() {
		this.titleService.setTitle('Touchplan');
		this.logger.log('actually clean up plan.component');
		this.shutdownSubject.next(true);
		this.unsubscribeList.forEach((sub) => sub.unsubscribe());
		this.stuffToDestroy.forEach((stuff) => stuff.destroy());
		this.stuffToDestroy = null;
		this.stuffToComplete.forEach((stuff) => stuff.complete());

		// BEGIN PLACEHOLDER REPLACE AS PART OF TP-4914
		if (this.tempDock && this.tempDock.destroy) {
			this.tempDock.destroy();
		}
		// END PLACEHOLDER REPLACE AS PART OF TP-4914

		this.planUndosService.destroyOperations();
		this.planAccessCheck.destroy();

		// MOC-3847 - Purge loaded font glyphs from memory. On next plan load, the cache will be re-generated from localStorage
		fontManager.purge();
	}

	//boom the planState when this is destroyed, should be able to use the subscription to planState's change event to actually clean up
	public ngOnDestroy() {
		this.planState.boom();
	}

	/**
	 * This function listens for Angular router events, also the Angular router state, and the plan state
	 * to open the "Ticket Import" dialog when the user transitions from the "Select Plan Import Dialog" in the
	 * projects screen to continue the importing process.
	 */
	private listenOpenImportTicketsDialog(): void {
		combineLatest([
			this.router.events.pipe(
				filter((event) => event instanceof NavigationEnd),
				map(() => this.router.getCurrentNavigation())
			),
			this.planState.stateChange$.pipe(filter((state) => state === StateEvents.FULLY_LOADED)),
		])
			.pipe(
				switchMap(([navigation]) =>
					combineLatest([
						this.featureFlagManager.getFlag$(DynamicFlagTypes.TICKET_IMPORT_V2_ENABLED),
						of(navigation),
					])
				),
				takeUntil(this.shutdownSubject)
			)
			.subscribe(([isTicketImportEnabled, navigation]) => {
				if (isTicketImportEnabled && navigation.extras.state?.openImportTicketsDialog) {
					this._location.replaceState(location.pathname, location.search, {
						...navigation.extras.state,
						openImportTicketsDialog: false,
					});
					openImportDialog(
						this.dialog,
						this.planState.projectId,
						this.planState.planId,
						this.planState.plansApi.data.list$,
						this.planState.locationsApi.data.list$,
						this.planState.rolesApi.data.list$,
						this.planState.tickets.list$,
						this.planState.variances.list$,
						this.planState.swimlanes.data.list$
					);
				}
			});
	}

	public onUnselectCompleted(completedTickets: Map<string, Ticket>, startedTickets: Map<string, Ticket>) {
		const unselectedTickets: Map<string, Ticket> = new Map([...completedTickets, ...startedTickets]);
		this.planState.actions.unSelectTicket(unselectedTickets);
	}

	// BEGIN PLACEHOLDER REPLACE AS PART OF TP-7317
	// - is the _right_ place for this?
	private listenToLodEmitter() {
		LevelOfDetail.emitter.on(
			LEVEL_OF_DETAIL_SIGNALS.CHANGED,
			(currentLODLevel: number, lastLODLevel: number) => {
				if (
					currentLODLevel >= LEVEL_OF_DETAIL_TABLE.GTP_MAXIMUM &&
					lastLODLevel < LEVEL_OF_DETAIL_TABLE.GTP_MAXIMUM
				) {
					this.granularEnabledService.setRenderingMode(TicketScale.Hourly);
					return;
				}
				if (
					currentLODLevel < LEVEL_OF_DETAIL_TABLE.GTP_MAXIMUM &&
					lastLODLevel >= LEVEL_OF_DETAIL_TABLE.GTP_MAXIMUM
				) {
					this.granularEnabledService.setRenderingMode(TicketScale.Daily);
				}
			}
		);
	}
	// END PLACEHOLDER REPLACE AS PART OF TP-7317
}
